import React from "react";
import { Typography, Box } from "@mui/material";
import Link from "@mui/material/Link";
import { Survey } from "survey-react-ui";
import CustomSurveyNav from "../components/CustomSurveyNav";
import { Tooltip } from "@mui/material";

// Determina si el botón "Retiré este curso" debe mostrarse
const shouldShowWithdrawButton = (status, questionnaireType) => {
  if (status === "not_evaluable" || status === "finished" || status === "withdrawn" || status === "started") return false;
  if (questionnaireType === "withdrawal") return false;
  return true;
};

// Formatea una fecha ISO a hora local de Bogotá
const formatBogotaDate = (isoString) => {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat("es-CO", {
      timeZone: "America/Bogota",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  } catch {
    return null;
  }
};

export const generateButton = (
  status,
  questionnaireId,
  currentSurveyId,
  handleButtonClick,
  disabledButton,
  questionnaireType,
  handleWithdrawClick,
) => {
  if (status === "not_evaluable" || status === "withdrawn") return [];

  const buttons = [];

  if (status === "pending") {
    buttons.push({
      buttonText: "Iniciar",
      onClick: () => handleButtonClick(questionnaireId, status, currentSurveyId),
      disabled: disabledButton,
    });
  }

  if (status === "started") {
    buttons.push({
      buttonText: "Continuar",
      onClick: () => handleButtonClick(questionnaireId, status, currentSurveyId),
      disabled: disabledButton,
    });
  }

  if (shouldShowWithdrawButton(status, questionnaireType)) {
    buttons.push({
      buttonText: "Retiré este curso",
      onClick: () => handleWithdrawClick(questionnaireId),
      disabled: disabledButton,
      color: "error",
    });
  }

  return buttons;
};

export function generateAppStatus(status) {
  if (status === "pending") return "Pendiente";
  if (status === "incomplete") return "Incompleto";
  return "Completado";
}

export function generateQuestionnaireStatus(status) {
  if (status === "pending") return "Pendiente";
  if (status === "finished") return "Terminado";
  if (status === "not_evaluable") return "No evaluable";
  return "Iniciado";
}

//Columnas de la tabla
export const columns = [
  { id: "periodo", label: "Periodo", minWidth: 90, align: "left" },
  { id: "categoria", label: "Categoría", minWidth: 120, align: "left" },
  { id: "nombre_curso", label: "Curso", minWidth: 180, align: "left" },
  { id: "codigo_curso", label: "Código", minWidth: 90, align: "left" },
  { id: "facultad", label: "Facultad", minWidth: 140, align: "left" },

  { id: "status", label: "Estado", minWidth: 125, align: "left" },
  { id: "actions", label: "Acciones", minWidth: 200, align: "left" },
];

const safeText = (v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "string" && v.trim() === "") return "-";
  return String(v);
};

export const generateRows = (data, handleButtonClick, disabledButton, handleWithdrawClick) => {
  if (!data) return [];

  const items = data.assignment_questionnaires || [];

  return items.map((item) => {
    const aq = item?.assignapp_questionnaire;
    const status = aq?.status;
    const ctx = aq?.context || {};

    const periodo = safeText(ctx.periodo);
    const categoria = safeText(ctx.categoria);
    const nombre_curso = safeText(ctx.nombre_curso);
    const codigo_curso = safeText(ctx.codigo_curso);
    const facultad = safeText(ctx.facultad);

    // Fila informativa sin acciones
    if (status === "not_evaluable") {
      return createData({ periodo, categoria, nombre_curso, codigo_curso, facultad, status: "No evaluable", actions: [] });
    }
    // Flujo normal
    const surveyId = item?.survey_id ?? null;
    const assignappQuestionnaireId = aq?.id ?? null;
    const statusText = status === "withdrawn" ? "No tomé este curso" : generateQuestionnaireStatus(status);
    const flowEnabled = item?.enabled ?? true;
    const buttonDisabled = disabledButton || !flowEnabled;

    const questionnaireType = item?.questionnaire_type ?? null;
    const actions = generateButton(
      status,
      String(assignappQuestionnaireId),
      surveyId,
      handleButtonClick,
      buttonDisabled,
      questionnaireType,
      handleWithdrawClick,
    );

    const formattedFinishedDate = status === "finished" ? formatBogotaDate(aq?.finished_at) : null;

    const statusCell = formattedFinishedDate ? (
      <Tooltip title={`Terminado el ${formattedFinishedDate}`} arrow placement="top" enterTouchDelay={50}>
        <span
          style={{
            cursor: "help",
            borderBottom: "1px dashed currentColor",
          }}
        >
          {statusText}
        </span>
      </Tooltip>
    ) : (
      statusText
    );

    return createData({ status: statusCell, periodo, categoria, nombre_curso, codigo_curso, facultad, actions });
  });
};

export function createData(row) {
  return row;
}

export const generateBreadcrumbs = (assignappQuestionnaireId, appName, handleBreadcrumbQuestionnaire) => {
  if (!assignappQuestionnaireId) {
    return [
      <Typography underline="none" key="1" color="inherit" sx={{ fontSize: "14px" }}>
        Página Principal
      </Typography>,
      <Typography key="3" color="secondary" sx={{ fontSize: "14px" }}>
        {appName}
      </Typography>,
    ];
  }

  return [
    <Typography underline="none" key="1" color="inherit" sx={{ fontSize: "14px" }}>
      Página Principal
    </Typography>,
    <Link underline="none" key="2" color="inherit" sx={{ cursor: "pointer" }} onClick={handleBreadcrumbQuestionnaire}>
      {appName}
    </Link>,
    <Typography key="3" color="secondary" sx={{ fontSize: "14px" }}>
      Asignación cuestionario: {assignappQuestionnaireId}
    </Typography>,
  ];
};

const EXTRA_PANEL_SUFFIX = "__extra";

const LOOKUP_SELECT_PREFIX = "__pd_lookup_select__";
const MANUAL_ENTRY_PREFIX = "__pd_manual_entry__";
const MANUAL_NAME_PREFIX = "__pd_manual_name__";
const MANUAL_EMAIL_PREFIX = "__pd_manual_email__";
const BODY_PANEL_PREFIX = "__pd_body__";

const isLookupRuntimeKey = (key) => {
  if (!key) return false;

  return (
    key.startsWith(LOOKUP_SELECT_PREFIX) ||
    key.startsWith(MANUAL_ENTRY_PREFIX) ||
    key.startsWith(MANUAL_NAME_PREFIX) ||
    key.startsWith(MANUAL_EMAIL_PREFIX) ||
    key.startsWith(BODY_PANEL_PREFIX)
  );
};

const stripLookupRuntimeKeys = (row) => {
  if (!row || typeof row !== "object") return row;

  return Object.fromEntries(Object.entries(row).filter(([key]) => !isLookupRuntimeKey(key)));
};

const isRuntimeAuxQuestion = (questionName) => {
  if (!questionName) return false;

  return [
    "__pd_want_extra__",
    "__pd_extra_count__",
    "__pd_extra_select__",
    "__pd_count_select__",
    LOOKUP_SELECT_PREFIX,
    MANUAL_ENTRY_PREFIX,
    MANUAL_NAME_PREFIX,
    MANUAL_EMAIL_PREFIX,
    BODY_PANEL_PREFIX,
  ].some((prefix) => questionName.startsWith(prefix));
};

const isExtraPanelName = (name) => typeof name === "string" && name.endsWith(EXTRA_PANEL_SUFFIX);

const basePanelNameOfExtra = (name) => name.replace(/__extra$/, "");

const isManualLookupSelected = (value) => Array.isArray(value) && value.includes("manual");

const extractLookupSelectRowData = (row) => {
  if (!row || typeof row !== "object") return row;

  const runtimeKeys = Object.keys(row).filter(isLookupRuntimeKey);
  const hasRuntimeLookupData = runtimeKeys.length > 0;

  // Si hay datos runtime, normaliza desde ahí, porque representan el estado actual del cuestionario.
  if (hasRuntimeLookupData) {
    const result = stripLookupRuntimeKeys(row);

    const lookupKey = Object.keys(row).find((k) => k.startsWith(LOOKUP_SELECT_PREFIX));
    const manualEntryKey = Object.keys(row).find((k) => k.startsWith(MANUAL_ENTRY_PREFIX));
    const manualNameKey = Object.keys(row).find((k) => k.startsWith(MANUAL_NAME_PREFIX));
    const manualEmailKey = Object.keys(row).find((k) => k.startsWith(MANUAL_EMAIL_PREFIX));

    const manualSelected = isManualLookupSelected(row[manualEntryKey]);

    if (manualSelected) {
      result.lookup_mode = "manual";
      result.manual_name = row[manualNameKey] || "";
      result.manual_email = row[manualEmailKey] || "";
      result.element_id = null;
    } else {
      result.lookup_mode = "list";
      result.element_id = row[lookupKey] || null;
      result.manual_name = "";
      result.manual_email = "";
    }

    return result;
  }

  if (row.lookup_mode === "manual" || row.lookup_mode === "list") {
    const result = stripLookupRuntimeKeys(row);

    if (result.lookup_mode === "manual") {
      result.element_id = null;
      result.manual_name = result.manual_name || "";
      result.manual_email = result.manual_email || "";
      result.element_text = null;
    } else {
      result.lookup_mode = "list";
      result.element_id = result.element_id || null;
      result.manual_name = "";
      result.manual_email = "";
    }

    return result;
  }

  return stripLookupRuntimeKeys(row);
};

const hydrateLookupSelectRowForRuntime = (row, panelName) => {
  if (!row || typeof row !== "object") return row;

  const cleanRow = extractLookupSelectRowData(row);

  const lookupKey = `${LOOKUP_SELECT_PREFIX}${panelName}`;
  const manualEntryKey = `${MANUAL_ENTRY_PREFIX}${panelName}`;
  const manualNameKey = `${MANUAL_NAME_PREFIX}${panelName}`;
  const manualEmailKey = `${MANUAL_EMAIL_PREFIX}${panelName}`;

  const hydrated = { ...stripLookupRuntimeKeys(cleanRow) };

  if (cleanRow.lookup_mode === "manual") {
    hydrated[lookupKey] = null;
    hydrated[manualEntryKey] = ["manual"];
    hydrated[manualNameKey] = cleanRow.manual_name || "";
    hydrated[manualEmailKey] = cleanRow.manual_email || "";
    hydrated.element_id = null;
  } else {
    hydrated[lookupKey] = cleanRow.element_id || null;
    hydrated[manualEntryKey] = [];
    hydrated[manualNameKey] = "";
    hydrated[manualEmailKey] = "";
    hydrated.lookup_mode = "list";
    hydrated.manual_name = "";
    hydrated.manual_email = "";
    // Preserva element_text para que preloadedChoices use el texto
    if (cleanRow.element_text) {
      hydrated.element_text = cleanRow.element_text;
    }
  }

  return hydrated;
};

// Función helper que construye un map entre questionId y su sectionName
const buildSectionMap = (surveyModel) => {
  const map = new Map();
  const pages = surveyModel?.toJSON?.()?.pages || surveyModel?.pages || [];

  pages.forEach((page) => {
    (page.elements || []).forEach((el) => {
      if (el.type === "panel" || el.type === "paneldynamic") {
        const sectionName = el.name;
        // Las preguntas hijas del panel
        (el.elements || el.templateElements || []).forEach((child) => {
          map.set(child.name, sectionName);
        });
      }
      // Si la pregunta está directamente en la página (sin panel) no tendrá sectionName
    });
  });

  return map;
};

export const surveyContentFormatter = (results, surveyModel) => {
  if (!results) return [];

  const sectionMap = buildSectionMap(surveyModel);

  const mergedPanelAnswers = new Map();
  const output = [];

  Object.keys(results).forEach((questionName) => {
    const question = surveyModel.getQuestionByName(questionName);
    if (!question) return;

    const answer = results[questionName];
    const type = question?.getType?.();

    // Consolida panel extra dentro del panel base
    if (type === "paneldynamic" && isExtraPanelName(questionName)) {
      const baseName = basePanelNameOfExtra(questionName);
      const current = mergedPanelAnswers.get(baseName) || [];
      mergedPanelAnswers.set(baseName, [...current, ...(Array.isArray(answer) ? answer : [])]);
      return;
    }

    if (type === "paneldynamic") {
      const current = mergedPanelAnswers.get(questionName) || [];
      mergedPanelAnswers.set(questionName, [...current, ...(Array.isArray(answer) ? answer : [])]);
      return;
    }

    output.push({
      questionTitle: question?.title,
      questionType: type,
      questionAnswer: answer,
      questionId: question?.name,
      sectionName: sectionMap.get(question?.name) ?? question?.name,
    });
  });

  mergedPanelAnswers.forEach((mergedAnswer, panelName) => {
    const question = surveyModel.getQuestionByName(panelName);
    if (!question) return;

    const panelMode = String(question?.jsonObj?.x_dynamic_mode || "").toLowerCase();

    let normalizedAnswer = mergedAnswer;

    if (panelMode === "lookup_select") {
      normalizedAnswer = (Array.isArray(mergedAnswer) ? mergedAnswer : []).map((row) => {
        const cleaned = extractLookupSelectRowData(row);

        // Si el modo es list, intenta capturar el texto visible del dropdown
        // para poder mostrarlo correctamente al restaurar
        if (cleaned.lookup_mode !== "manual" && cleaned.element_id) {
          const pd = surveyModel.getQuestionByName(panelName);
          const panel = pd?.panels?.find((p) => {
            const lookupQ = p.getQuestionByName(`__pd_lookup_select__${panelName}`);
            return lookupQ?.value === cleaned.element_id;
          });

          if (panel) {
            const lookupQ = panel.getQuestionByName(`__pd_lookup_select__${panelName}`);
            const selectedItem = lookupQ?.selectedItem;
            if (selectedItem?.text) {
              cleaned.element_text = selectedItem.text;
            }
          }
        }

        return cleaned;
      });
    }

    // Captura el label del dropdown element_id para count_select
    if (panelMode === "count_select") {
      normalizedAnswer = (Array.isArray(mergedAnswer) ? mergedAnswer : []).map((row, rowIndex) => {
        if (!row?.element_id) return row;

        // Busca el panel correspondiente en el modelo
        const pd = surveyModel.getQuestionByName(panelName);
        const panel = pd?.panels?.[rowIndex];

        if (!panel) return row;

        const dropdownQ = panel.getQuestionByName("element_id");
        if (!dropdownQ) return row;

        // Busca el texto en los choices del dropdown
        const selectedChoice = dropdownQ.choices?.find((c) => c.value == row.element_id);
        const elementText = selectedChoice?.text || dropdownQ.selectedItem?.text || null;

        if (!elementText) return row;

        return { ...row, element_text: elementText };
      });
    }

    output.push({
      questionTitle: question?.title,
      questionType: question?.getType?.(),
      questionAnswer: normalizedAnswer,
      questionId: panelName,
      sectionName: sectionMap.get(panelName) ?? panelName,
    });
  });

  return output;
};

export const generateQuestionnaireContent = ({ questionnaire, questionnaireError = null, disabled = false, onFinish, disableFinish = false }) => {
  if (!questionnaire) {
    return <Box sx={{ padding: 2 }}>{questionnaireError || "Cargando cuestionario..."}</Box>;
  }

  return (
    <Box sx={{ width: "100%", height: "max-content", "& .sd-root-modern": { overflowY: "hidden" } }}>
      <Box sx={{ mb: 2 }}>
        <CustomSurveyNav model={questionnaire} disabled={disabled} onFinish={onFinish} disableFinish={disableFinish} />
      </Box>
      <Survey model={questionnaire} />
      <CustomSurveyNav model={questionnaire} disabled={disabled} onFinish={onFinish} disableFinish={disableFinish} />
    </Box>
  );
};

const parseRepeatLimit = (raw) => {
  if (raw === undefined || raw === null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const applyDefaultValuesToSurvey = (surveyJson, savedContent) => {
  const savedMap = new Map((savedContent || []).map((it) => [it.questionId, it.questionAnswer]));

  const updatedPages = surveyJson?.pages?.map((page) => {
    const updatedElements = page?.elements?.map((el) => {
      const baseQuestionId = isExtraPanelName(el?.name) ? basePanelNameOfExtra(el.name) : el?.name;
      const savedAnswer = savedMap.get(baseQuestionId);
      if (savedAnswer === undefined) return el;

      if (el.type === "paneldynamic") {
        const mode = String(el.x_dynamic_mode || "").toLowerCase();

        if (["count_select", "count_only", "lookup_select"].includes(mode)) {
          const savedRows = Array.isArray(savedAnswer) ? savedAnswer : [];

          if (mode === "lookup_select") {
            const restoredRows = savedRows.map((row) => extractLookupSelectRowData(row)).map((row) => hydrateLookupSelectRowForRuntime(row, el.name));

            const enrichedTemplateElements = (el.templateElements || []).map((te) => {
              const lookupKey = `__pd_lookup_select__${el.name}`;
              if (te.name !== lookupKey) return te;

              const preloadedChoices = restoredRows
                .map((row) => {
                  const val = row[lookupKey];
                  if (!val) return null;
                  // Usa element_text si fue guardado, si no cae al ID como fallback
                  const text = row.element_text || val;
                  return { value: val, text };
                })
                .filter(Boolean);

              return {
                ...te,
                choices: preloadedChoices,
              };
            });

            return {
              ...el,
              templateElements: enrichedTemplateElements,
              panelCount: restoredRows.length,
              defaultValue: restoredRows,
            };
          }

          return {
            ...el,
            panelCount: savedRows.length,
            defaultValue: savedRows,
          };
        }

        const repeatKey = el.x_repeat_key || "id";
        const answerKeys = (el.templateElements || []).map((t) => t.name).filter(Boolean);

        const repeatLimit = parseRepeatLimit(el.x_repeat_limit);
        const extraLimit = parseRepeatLimit(el.x_extra_limit) ?? 1;

        const savedRows = Array.isArray(savedAnswer) ? savedAnswer : [];

        // El panel extra recibe solo la parte extra de la respuesta consolidada
        if (isExtraPanelName(el.name)) {
          const sourcePanelName = el.x_source_panel_name || basePanelNameOfExtra(el.name);
          const mergedSavedAnswer = savedMap.get(sourcePanelName);
          const mergedRows = Array.isArray(mergedSavedAnswer) ? mergedSavedAnswer : [];

          const sourceRepeatLimit =
            parseRepeatLimit(el.x_repeat_limit) ??
            parseRepeatLimit((page?.elements || []).find((item) => item?.name === sourcePanelName)?.x_repeat_limit);

          const sourceExtraLimit =
            parseRepeatLimit(el.x_extra_limit) ??
            parseRepeatLimit((page?.elements || []).find((item) => item?.name === sourcePanelName)?.x_extra_limit) ??
            1;

          const extraRows = sourceRepeatLimit ? mergedRows.slice(sourceRepeatLimit, sourceRepeatLimit + sourceExtraLimit) : [];

          return {
            ...el,
            panelCount: extraRows.length,
            defaultValue: extraRows,
          };
        }

        // Panel base
        const baseRows = repeatLimit ? savedRows.slice(0, repeatLimit) : savedRows;
        const baseDefault = Array.isArray(el.defaultValue) ? el.defaultValue : baseRows;

        const merged = baseDefault.map((item) => {
          const keyVal = item?.[repeatKey];
          const savedItem = baseRows.find((r) => r && r[repeatKey] === keyVal);
          if (!savedItem) return item;

          const onlyAnswers = {};
          answerKeys.forEach((k) => {
            if (Object.prototype.hasOwnProperty.call(savedItem, k)) onlyAnswers[k] = savedItem[k];
          });

          return { ...item, ...onlyAnswers };
        });

        return {
          ...el,
          panelCount: merged.length,
          defaultValue: merged,
        };
      }

      return { ...el, defaultValue: savedAnswer };
    });

    const pdCountDefaults = new Map();
    (updatedElements || []).forEach((el) => {
      if (el?.type === "paneldynamic" && ["count_select", "count_only", "lookup_select"].includes(String(el?.x_dynamic_mode || "").toLowerCase())) {
        const qName = el?.x_count_question || `__pd_count_select__${el.name}`;
        const count = Array.isArray(el.defaultValue) ? el.defaultValue.length : 0;
        pdCountDefaults.set(qName, count);
      }
    });

    const extraRuntimeDefaults = new Map();
    (updatedElements || []).forEach((el) => {
      if (el?.type === "paneldynamic" && isExtraPanelName(el?.name)) {
        const basePanelName = basePanelNameOfExtra(el.name);
        const countName = `__pd_extra_count__${basePanelName}`;
        const wantName = `__pd_want_extra__${basePanelName}`;
        const count = Array.isArray(el.defaultValue) ? el.defaultValue.length : 0;

        extraRuntimeDefaults.set(countName, count);
        extraRuntimeDefaults.set(wantName, count > 0);
      }
    });

    const updatedElements2 = (updatedElements || []).map((el) => {
      const saved = savedMap.get(el.name);

      if (saved !== undefined) {
        if (el?.type === "paneldynamic" && String(el?.x_dynamic_mode || "").toLowerCase() === "lookup_select") {
          const savedRows = Array.isArray(saved) ? saved : [];
          const restoredRows = savedRows.map((row) => extractLookupSelectRowData(row)).map((row) => hydrateLookupSelectRowForRuntime(row, el.name));

          return {
            ...el,
            panelCount: restoredRows.length,
            defaultValue: restoredRows,
          };
        }

        return el;
      }

      if (pdCountDefaults.has(el.name)) {
        const count = pdCountDefaults.get(el.name);

        if (count === 0) return el;

        return { ...el, defaultValue: count };
      }

      if (extraRuntimeDefaults.has(el.name)) {
        return { ...el, defaultValue: extraRuntimeDefaults.get(el.name) };
      }

      const parsedSelect = parseIndexedExtraSelect(el.name);
      if (parsedSelect) {
        const basePanelName = parsedSelect.panelName;
        const savedMergedAnswer = savedMap.get(basePanelName);
        const mergedRows = Array.isArray(savedMergedAnswer) ? savedMergedAnswer : [];

        const basePanel = (updatedElements || []).find((item) => item?.name === basePanelName);
        const extraPanel = (updatedElements || []).find((item) => item?.name === `${basePanelName}__extra`);

        const repeatLimit = parseRepeatLimit(extraPanel?.x_repeat_limit) ?? parseRepeatLimit(basePanel?.x_repeat_limit);

        const extraLimit = parseRepeatLimit(extraPanel?.x_extra_limit) ?? parseRepeatLimit(basePanel?.x_extra_limit) ?? 1;

        const repeatKey = extraPanel?.x_repeat_key || basePanel?.x_repeat_key || "id";

        const selectedExtra = repeatLimit ? mergedRows.slice(repeatLimit, repeatLimit + extraLimit) : [];
        const row = selectedExtra[parsedSelect.index - 1];

        if (!row) return el;

        return { ...el, defaultValue: row?.[repeatKey] };
      }

      return el;
    });

    return { ...page, elements: updatedElements2 };
  });

  return { ...surveyJson, pages: updatedPages };
};

// Lógica para el manejo de paneles dinámicos con adicionales
const PD_WANT_PREFIX = "__pd_want_extra__";
const PD_EXTRA_COUNT_PREFIX = "__pd_extra_count__";
const PD_SELECT_PREFIX = "__pd_extra_select__";

const PD_COUNT_PREFIX = "__pd_count_select__";

const parsePanelName = (name, prefix) => (name?.startsWith(prefix) ? name.slice(prefix.length) : null);

const parseIndexedExtraSelect = (name) => {
  const m = name?.match(/^__pd_extra_select__(.+)__([0-9]+)$/);
  if (!m) return null;

  return {
    panelName: m[1],
    index: Number(m[2]),
  };
};

const isCountSelectPanel = (pdJson) => ["count_select", "count_only", "lookup_select"].includes(String(pdJson?.x_dynamic_mode || "").toLowerCase());

const normalizeArrayValue = (v) => (Array.isArray(v) ? v : []);

const ensurePanelCountAndValue = (pd, desiredCount) => {
  const q = pd;
  const count = Math.max(0, Number(desiredCount) || 0);
  q.panelCount = count;

  const current = normalizeArrayValue(q.value);

  if (count === 0) {
    q.value = [];
    return;
  }

  if (current.length > count) {
    q.value = current.slice(0, count);
    return;
  }

  if (current.length < count) {
    const missing = count - current.length;
    const fill = Array.from({ length: missing }, () => ({}));
    q.value = [...current, ...fill];
  }
};

const getKeyVal = (obj, repeatKey) => obj?.[repeatKey] ?? obj?.[String(repeatKey)];

const getExtraPanelRuntimeInfo = (sender, basePanelName) => {
  const extraPanelName = `${basePanelName}__extra`;
  const pd = sender.getQuestionByName(extraPanelName);
  if (!pd) return null;

  const json = pd?.jsonObj || {};
  const repeatKey = json.x_repeat_key || "id";
  const extraLimit = parseRepeatLimit(json.x_extra_limit) ?? 1;
  const extraItems = Array.isArray(json.x_extra_items) ? json.x_extra_items : [];

  return { pd, repeatKey, extraLimit, extraItems };
};

const rebuildExtraPanelValue = ({ sender, panelName }) => {
  const info = getExtraPanelRuntimeInfo(sender, panelName);
  if (!info) return false;

  const { pd, repeatKey, extraLimit, extraItems } = info;

  const wantName = `${PD_WANT_PREFIX}${panelName}`;
  const countName = `${PD_EXTRA_COUNT_PREFIX}${panelName}`;

  const wantsExtra = sender.getValue(wantName) === true;
  const rawCountValue = sender.getValue(countName);

  const selectedCount = wantsExtra
    ? rawCountValue !== undefined && rawCountValue !== null && rawCountValue !== ""
      ? Number(rawCountValue || 0)
      : extraLimit === 1
        ? 1
        : 0
    : 0;

  if (!wantsExtra || selectedCount <= 0) {
    pd.value = [];
    pd.panelCount = 0;
    return true;
  }

  const selectedIds = [];
  for (let i = 1; i <= Math.min(selectedCount, extraLimit); i += 1) {
    const selectName = `${PD_SELECT_PREFIX}${panelName}__${i}`;
    const val = sender.getValue(selectName);
    if (val !== undefined && val !== null && val !== "") {
      selectedIds.push(val);
    }
  }

  const uniqueIds = [...new Set(selectedIds)];

  const currentRows = Array.isArray(pd.value) ? pd.value : [];

  const extraRows = uniqueIds
    .map((id) => {
      const baseItem = extraItems.find((it) => getKeyVal(it, repeatKey) === id);
      if (!baseItem) return null;

      const existingRow = currentRows.find((row) => getKeyVal(row, repeatKey) === id);

      return existingRow ? { ...baseItem, ...existingRow } : baseItem;
    })
    .filter(Boolean);

  pd.panelCount = extraRows.length;
  pd.value = extraRows;

  return true;
};

export const handlePanelDynamicExtraControlsChange = ({ sender, options }) => {
  const name = options?.name;

  const panelFromWant = parsePanelName(name, PD_WANT_PREFIX);
  if (panelFromWant) {
    if (options?.value === false) {
      const countName = `${PD_EXTRA_COUNT_PREFIX}${panelFromWant}`;
      if (sender.getQuestionByName(countName)) {
        sender.setValue(countName, null);
      }

      const info = getExtraPanelRuntimeInfo(sender, panelFromWant);
      if (info) {
        for (let i = 1; i <= info.extraLimit; i += 1) {
          sender.setValue(`${PD_SELECT_PREFIX}${panelFromWant}__${i}`, null);
        }
      }
    }

    return rebuildExtraPanelValue({ sender, panelName: panelFromWant });
  }

  const panelFromCount = parsePanelName(name, PD_EXTRA_COUNT_PREFIX);
  if (panelFromCount) {
    const desired = Number(options?.value || 0);
    const info = getExtraPanelRuntimeInfo(sender, panelFromCount);

    if (info) {
      for (let i = desired + 1; i <= info.extraLimit; i += 1) {
        sender.setValue(`${PD_SELECT_PREFIX}${panelFromCount}__${i}`, null);
      }
    }

    return rebuildExtraPanelValue({ sender, panelName: panelFromCount });
  }

  const parsedSelect = parseIndexedExtraSelect(name);
  if (parsedSelect) {
    return rebuildExtraPanelValue({ sender, panelName: parsedSelect.panelName });
  }

  return false;
};

export const handlePanelDynamicCountSelectChange = ({ sender, options }) => {
  const name = options?.name;

  const panelName = parsePanelName(name, PD_COUNT_PREFIX);
  if (!panelName) return false;

  const pd = sender.getQuestionByName(panelName);
  if (!pd) return false;

  const pdJson = pd?.jsonObj || {};
  if (!isCountSelectPanel(pdJson)) return false;

  const desired = Number(options?.value || 0);
  ensurePanelCountAndValue(pd, desired);

  return true;
};

const parseLookupPanelName = (name, prefix) => (name?.startsWith(prefix) ? name.slice(prefix.length) : null);

const rebuildLookupSelectPanelValue = ({ sender, panelName }) => {
  const pd = sender.getQuestionByName(panelName);
  if (!pd) return false;

  const currentRows = Array.isArray(pd.value) ? pd.value : [];
  const desiredCount = pd.panelCount || 0;

  const nextRows = Array.from({ length: desiredCount }, (_, index) => {
    const currentRow = stripLookupRuntimeKeys(currentRows[index] || {});
    const panel = pd.panels[index];
    const panelData = panel?.getValue() || {};

    const lookupName = `${LOOKUP_SELECT_PREFIX}${panelName}`;
    const manualEntryName = `${MANUAL_ENTRY_PREFIX}${panelName}`;
    const manualName = `${MANUAL_NAME_PREFIX}${panelName}`;
    const manualEmail = `${MANUAL_EMAIL_PREFIX}${panelName}`;

    const manualSelected = isManualLookupSelected(panelData[manualEntryName]);

    const baseRow = stripLookupRuntimeKeys(currentRow);

    if (manualSelected) {
      return {
        ...baseRow,
        lookup_mode: "manual",
        manual_name: panelData[manualName] || "",
        manual_email: panelData[manualEmail] || "",
        element_id: null,
        element_text: null,
      };
    }

    const newElementId = panelData[lookupName] || null;

    let elementText = baseRow.element_text || null;
    if (newElementId) {
      const lookupQ = panel?.getQuestionByName(lookupName);
      const selectedItem = lookupQ?.selectedItem;
      if (selectedItem?.text) {
        elementText = selectedItem.text;
      }
    }

    return {
      ...baseRow,
      lookup_mode: "list",
      element_id: newElementId,
      manual_name: "",
      manual_email: "",
      ...(elementText ? { element_text: elementText } : {}),
    };
  });

  pd.value = nextRows;
  return true;
};

export const handlePanelDynamicLookupSelectChange = ({ sender, options }) => {
  const name = options?.name;
  if (!name) return false;

  const panelName =
    parseLookupPanelName(name, LOOKUP_SELECT_PREFIX) ||
    parseLookupPanelName(name, MANUAL_ENTRY_PREFIX) ||
    parseLookupPanelName(name, MANUAL_NAME_PREFIX) ||
    parseLookupPanelName(name, MANUAL_EMAIL_PREFIX);

  if (!panelName) return false;

  return rebuildLookupSelectPanelValue({ sender, panelName });
};
